/**
 * EARLYWIRE External API Contract — v1 (TypeScript)
 * ---------------------------------------------------------------------------
 * The interface the frontend uses to load data from the LATL backend over the
 * WebSocket at `wss://api.earlywire.io/api/ws`, plus the REST shapes for
 * `/api/me` and signal detail.
 *
 * THREE CORE NOUNS:
 *   · TrackedObject — a live mover (vessel / aircraft / satellite)
 *   · GeoEvent      — something that happened at a place/time
 *   · Insight       — a rule-engine signal (THE product); upsert-by-id, revised in place
 */

/* ───────────────────────── Severity ───────────────────────── */

/** Mandated severity tokens. Do not invent others. */
export type Severity = "low" | "medium" | "high" | "critical";

/** Brand-mandated severity colors. Keep in sync with styles/tokens.css. */
export const SEVERITY_COLOR: Record<Severity, string> = {
  low: "#8b9dc3",
  medium: "#f4b942",
  high: "#e8743b",
  critical: "#d62839",
};

export const SEVERITY_RANK: Record<Severity, number> = {
  critical: 4,
  high: 3,
  medium: 2,
  low: 1,
};

/* ───────────────────────── Domains ───────────────────────── */

export type Domain = "maritime" | "aircraft" | "satellite";

/** Event types as emitted by the backend — EXACT strings. The WS subscription
 *  and the map filter both match on these verbatim. Mirrors @latl/shared. */
export type EventType =
  | "earthquake"
  | "weather"
  | "power_outage"
  | "gas_disruption"
  | "industrial_fire"
  | "news"
  | "conflict"
  | "airspace_closure"
  | "maritime_warning"
  | "gps_jamming"
  | "ais_gap"
  | "dark_vessel";

/** Drives the event-type filter UI. `defaultOn` = pre-selected for every
 *  operator; the rest are opt-in ("additional events relevant to them"). */
export interface EventTypeMeta {
  type: EventType;
  label: string;
  defaultOn: boolean;
}
export const EVENT_TYPE_CATALOG: EventTypeMeta[] = [
  { type: "power_outage", label: "Power", defaultOn: true },
  { type: "gas_disruption", label: "Gas", defaultOn: true },
  { type: "industrial_fire", label: "Fire", defaultOn: true },
  { type: "earthquake", label: "Quake", defaultOn: true },
  { type: "conflict", label: "Conflict", defaultOn: true },
  { type: "airspace_closure", label: "Airspace", defaultOn: true },
  { type: "maritime_warning", label: "Maritime", defaultOn: true },
  { type: "gps_jamming", label: "Jamming", defaultOn: true },
  { type: "news", label: "News", defaultOn: true },
  { type: "weather", label: "Weather", defaultOn: false },
  { type: "ais_gap", label: "AIS Gap", defaultOn: false },
  { type: "dark_vessel", label: "Dark Ship", defaultOn: false },
];
export const ALL_EVENT_TYPES: EventType[] = EVENT_TYPE_CATALOG.map((e) => e.type);
export const DEFAULT_EVENT_TYPES: EventType[] = EVENT_TYPE_CATALOG.filter((e) => e.defaultOn).map(
  (e) => e.type,
);

/* ───────────────────────── Trading areas ───────────────────────── */

/** Desk-facing signal groupings (mirror the backend rule `category`). The CORE
 *  feed shows high/critical signals across all areas; enabling an area ADDS its
 *  full lower-severity (medium/low) detail to the feed. */
export type TradingArea = "energy" | "maritime" | "geopolitical" | "natural";

export interface TradingAreaMeta {
  area: TradingArea;
  label: string;
}
export const TRADING_AREAS: TradingAreaMeta[] = [
  { area: "energy", label: "Energy" },
  { area: "maritime", label: "Maritime" },
  { area: "geopolitical", label: "Geopolitical" },
  { area: "natural", label: "Natural" },
];

/** signal rule → trading area. Keep in sync with each rule's `category`. */
export const RULE_AREA: Record<string, TradingArea> = {
  "power-outage-impact": "energy",
  "outage-cluster": "energy",
  "industrial-fire-impact": "energy",
  "sanctioned-vessel": "maritime",
  "vessel-in-jamming-zone": "maritime",
  "news-near-chokepoint": "geopolitical",
  "military-near-chokepoint": "geopolitical",
  "earthquake-near-asset": "natural",
  "severe-event-near-asset": "natural",
  "quake-company-impact": "natural",
};

/* ───────────────────────── Tracked objects ───────────────────────── */

/** Raw object as it arrives on the wire (snapshot/positions frames). */
export interface WireObject {
  id: string;
  domain: Domain;
  lat: number | string;
  lon: number | string;
  heading?: number;
  speed?: number;
  name?: string | null;
  lastSeen?: number;
}

/** Normalized + cached object held in the client store. */
export interface TrackedObject {
  id: string;
  domain: Domain;
  lat: number;
  lon: number;
  heading?: number;
  speed?: number;
  name?: string | null;
  lastSeen?: number;
  /** client-side: ms timestamp the object was last refreshed (for TTL sweep) */
  seenAt?: number;
  /** client-side: which shard socket delivered it */
  shard?: string;
  /** client-side cached Mercator world coords (render hot-path) */
  _wx?: number;
  _wy?: number;
  _wlon?: number;
  _wlat?: number;
}

/* ───────────────────────── Geo events ───────────────────────── */

export interface GeoEvent {
  id: string;
  type: EventType;
  title?: string;
  summary?: string;
  severity?: Severity;
  lat: number;
  lon: number;
  /** impact radius in km (optional; drives the dashed ring on the plot) */
  radius?: number;
  source?: string;
  sourceUrl?: string;
  timestamp?: number;
  metadata?: {
    magnitude?: number;
    magtype?: string;
    depthKm?: number;
    place?: string;
    authority?: string;
    [k: string]: unknown;
  };
}

/* ───────────────────────── Insights / Signals ───────────────────────── */

export interface AffectedAsset {
  name: string;
  type?: string;
  distanceKm?: number;
  ticker?: string;
  country?: string;
  /** site coordinates (RefAsset carries them on the wire) — lets the map highlight the asset */
  lat?: number;
  lon?: number;
}

/** 3-axis confidence — only present on REST signal-detail, not the WS feed. */
export interface Confidence {
  occurrence: number; // did it happen
  intensity: number; // how big
  attribution: number; // who / what
}

/** Raw insight as it arrives on the wire. */
export interface WireInsight {
  id: string;
  title?: string;
  summary?: string;
  ruleName?: string;
  severity: Severity;
  tickers?: string[];
  recommendedAction?: string;
  lat: number | string;
  lon: number | string;
  affectedAssets?: AffectedAsset[];
  triggerEventIds?: string[];
  triggerObjectIds?: string[];
  confidence?: Confidence | null;
  confidenceRate?: number;
  estimatedEffect?: { sigma: number; pct: number } | null;
  createdAt?: number;
  updatedAt?: number;
  status?: 'live' | 'resolved';
  resolvedAt?: number | null;
  statusReliable?: boolean;
  parentId?: string | null;
  rootId?: string | null;
  isRoot?: boolean;
  engineIngressAt?: number;
  enginePublishedAt?: number;
}

/** Normalized signal used throughout the UI. */
export interface Signal {
  id: string;
  title: string;
  severity: Severity;
  region: string;
  tickers: string[];
  rule: string;
  summary: string;
  recommendedAction: string;
  lat: number;
  lon: number;
  createdAt: number;
  updatedAt: number;
  assets: Array<{
    name: string;
    kind?: string;
    distKm?: number;
    ticker?: string;
    country?: string;
    lat?: number;
    lon?: number;
  }>;
  triggers: string[];
  confidence: Confidence | null;
  confidenceRate: number | null;
  estimatedEffect: { sigma: number; pct: number } | null;
  status: 'live' | 'resolved';
  resolvedAt: number | null;
  statusReliable: boolean;
  parentId: string | null;
  rootId: string | null;
  isRoot: boolean;
  engineIngressAt: number | null;
  enginePublishedAt: number | null;
  /** original wire payload, kept for forward-compat */
  raw: WireInsight;
}

/* ───────────────────────── WebSocket protocol ───────────────────────── */

export interface ViewportBounds {
  minLat: number;
  minLon: number;
  maxLat: number;
  maxLon: number;
}

/** Client → server. Subscribe (or re-subscribe) a socket to a slice of the world. */
export interface SubscribeMessage {
  type: "subscribe";
  viewport: ViewportBounds;
  domains: Domain[];
  eventTypes: EventType[];
  /** optional gap-fill cursor for reconnect */
  since?: number;
}

/** One desk-chat message ('system' = engine-posted signal anchor, signalId set). */
export interface ChatMessage {
  id: number;
  kind: "user" | "system";
  handle: string;
  body: string;
  signalId: string | null;
  at: number;
}

/** Server → client frames. */
export type ServerMessage =
  | { type: "snapshot"; objects?: WireObject[]; events?: GeoEvent[]; insights?: WireInsight[]; truncated?: boolean; serverTime?: number }
  | { type: "positions"; objects: WireObject[] }
  | { type: "remove"; ids: string[] }
  | { type: "signal"; signal: WireInsight }
  | { type: "event"; event: GeoEvent }
  | { type: "hb"; t?: number } // heartbeat, carries the server clock
  | { type: "chat"; msg: ChatMessage }
  | { type: "chat-remove"; id: number }
  | { type: "error"; message?: string };

/* ───────────────────────── REST shapes ───────────────────────── */

export interface MeResponse {
  email: string;
  /** The address AS TYPED at signup (users.contact_email) — show THIS to the human;
   *  `email` is the normalized dedup key (dots collapsed, +tags stripped). */
  contactEmail?: string | null;
  plan: string;
  status: string; // 'active' | 'pending' (= trial state) | 'suspended'
  /** Server-side gate verdict: false = expired trial or suspended. */
  entitled?: boolean;
  reason?: "trial_expired" | "suspended" | "blocked_email" | "email_unconfirmed" | "unknown_status" | null;
  /** End of the self-serve 1h trial (epoch ms); null once active or while comped. */
  trialEndsAt?: number | null;
  /** Complimentary access until (epoch ms) — admin FREE MONTH; null when none. */
  compUntil?: number | null;
  /** Referral code this account is attributed to (kickback tracking); null when organic. */
  referredBy?: string | null;
  displayName?: string;
  createdAt?: number;
  [k: string]: unknown;
}

/* ───────────────────────── Connection state ───────────────────────── */

export type ConnectionState =
  | "connecting"
  | "live"
  | "reconnecting"
  | "resynced"
  | "simulated"
  | "offline";
